// @flow
import moment from 'moment';
import { find, uniqBy, filter, get, forEach, map, toString, reduce, toNumber, sortBy, some, size, maxBy, has } from 'lodash';
import { getI18n } from 'react-i18next';
import { durations as baseDurations } from '@constants';
import { sort, numberCompare, formatMinutesToHoursMin, alphabeticCompare } from '@helpers/helpers';
import type { Patient, Doctors, Services, Service } from '@types';
import ServiceService from '../../../../../services/ServiceService';

export const getDurations = (selectedDuration: string, defaultAppointmentDuration: string) => {
  let durations;
  const durationExits = find(baseDurations, (d) => d.value === (selectedDuration || defaultAppointmentDuration));

  if (!durationExits) {
    const dur = selectedDuration || defaultAppointmentDuration;
    durations = [...baseDurations, { text: formatMinutesToHoursMin(dur), value: dur }];
  } else {
    durations = baseDurations;
  }

  durations = uniqBy(durations, 'value');
  durations = sort(durations, numberCompare('value'));

  return durations;
};


export const filterForceHidden = (items: any, isNew: boolean) => {
  const newItems = filter<any>(items, item => get(item, ['customRendererData', 'forceHidden'], false) !== true);

  if (isNew) {
    return filter<any>(newItems, item => get(item, ['customRendererData', 'action']) !== 'status');
  }

  return newItems;
};

export const sortPatients = (list: any) => sort(list, alphabeticCompare('name'));

export const mapSuggestedPatients = (suggestedPatients: Array<Patient>, doctors: Doctors) => {
  let suggested;
  if (suggestedPatients && suggestedPatients.length) {
    suggested = [
      {
        type: 'heading',
        text: getI18n().t('agenda:label/patient_select/suggested'),
      },
    ];
    forEach(sortPatients(suggestedPatients), (patient) => {
      const doctor = get(doctors, toString(patient.doctorId), {});
      const data = {
        customRendererData: {
          patientType: 'suggested',
          patient,
          doctor,
        },
      };
      suggested.push(data);
    });
  }
  return suggested;
};


export const mapSearchablePatients = (searchablePatients: Array<Patient>, doctors: Doctors, isListComplete: boolean, heading: boolean) => {
  let searchable;
  if (searchablePatients && searchablePatients.length) {
    searchable = heading ? [
      {
        type: 'heading',
      },
    ] : [];
    forEach(sortPatients(searchablePatients), (patient) => {
      const doctor = get(doctors, toString(patient.doctorId), {});
      const data = {
        customRendererData: {
          patientType: 'searchable',
          patient,
          doctor,
        },
      };
      searchable.push(data);
    });

    const patientsListNote = {
      className: 'patientsListNote',
      type: 'patientsListNote',
      text: getI18n().t('agenda:label/patient_select/incomplete_list'),
    };

    if (!isListComplete) {
      searchable.push(patientsListNote);
    }
  }

  return searchable;
};


export const mapServicesIds = (ids: Array<number>, servicesTemplates: Services) => map<any, any>(ids, id => get(servicesTemplates, ['data', toString(id)]));

export const createServicesHeading = (text: string) => ({
  type: 'heading',
  text,
  customRendererData: {
    type: 'heading',
  },
});

export const serviceToListItem = (service: Service) => ({
  type: !service.healthInsurance ? 'particular' : 'healthInsurance',
  className: 'listItem chk',
  key: service.id,
  customRendererData: {
    service,
  },
});

// When the appointment got to each status: of the kinds with the same badge (the three cancellations, the two
// confirmations), the latest, as the status screen dates it
const statusTimes = (data: Object) => {
  const latest = (...keys) => maxBy(filter(map(keys, (key) => get(data, key))), (time) => moment(time).valueOf() || 0) || null;
  return {
    missed: latest('patientMissedAt'),
    canceled: latest('canceledByDoctorAt', 'canceledByPatientAt', 'canceledBySmsAt'),
    completed: latest('finishedAt'),
    arrived: latest('patientArrivedAt'),
    confirmed: latest('confirmedAt', 'confirmedBySmsAt'),
  };
};

// The appointment's current status with the moment it got there. The API's badge when it's one of the five (what the
// agenda's cards show); otherwise the badge the API would give (Appointment#badge's order) from the status timestamps
export const currentStatus = (data: Object, badge: ?string): { badge: ?string, at: ?string } => {
  const times = statusTimes(data);
  if (badge && has(times, badge)) return { badge, at: times[badge] || null };
  const found = find(['missed', 'canceled', 'completed', 'arrived', 'confirmed'], (key) => times[key]);
  return { badge: found || null, at: found ? times[found] : null };
};

const isoWeekdays = {
  monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 7,
};

// "Toda semana: Seg, Qua · 10 consultas", "A cada 2 semanas · 10 consultas"; null when it doesn't repeat. A saved
// series comes back without its mode (formatRecurrent): savedBiweekly says it, else "Recorrente: Seg, Qua · 10
// consultas". The days as the repetition screen's chips name them. Blocked times count "horários" (unitKey; one is
// unitKey_one)
export const recurrenceSummary = (
  recurrent: ?Object, _: Function, unitKey: string = 'label/row/recurrence/appointments', savedBiweekly: boolean = false,
): ?string => {
  if (!get(recurrent, 'recurrentEnabled', false) && !get(recurrent, 'appointmentsCount')) return null;
  const mode = get(recurrent, 'mode') || (savedBiweekly ? 'biweekly' : null);
  const weekDays = sortBy(filter(get(recurrent, 'recurrentDays', []), (day) => isoWeekdays[day]), (day) => isoWeekdays[day]);
  const days = map(weekDays, (day) => _(`label/recurrency/short/${day}`)).join(', ');
  let how = days ? `${_('label/row/recurrence/other')}: ${days}` : _('label/row/recurrence/other');
  if (mode === 'weekly') how = days ? `${_('label/recurrency/weekly')}: ${days}` : _('label/recurrency/weekly');
  if (mode === 'biweekly') how = _('label/recurrency/biweekly');
  const count = get(recurrent, 'appointmentsCount');
  return count ? `${how} · ${count} ${_(toNumber(count) === 1 ? `${unitKey}_one` : unitKey)}` : how;
};

// "Consulta", "Consulta, Retorno", "Consulta, Retorno + 2", and their total. A template not loaded yet still counts
// (its name and price unknown: no total), and so does none when a template is priced per unit in a clinic selling by
// quantity: its price isn't what the line costs. A saved sale's price is its line total
export const servicesSummary = (services: Array<?Object>, byQuantity: boolean = false): ?{ names: string, total: ?number } => {
  if (!size(services)) return null;
  const known = filter(services, (service) => service && service.name);
  const unknownCount = size(services) - size(known);
  const names = map(known, 'name');
  const shown = names.slice(0, 2).join(', ');
  const others = names.length - Math.min(names.length, 2) + unknownCount;
  const perUnit = byQuantity && some(known, (service) => ServiceService.isTemplateByQuantity(service) && !ServiceService.isByQuantity(service));
  return {
    names: [shown, others ? `+ ${others}` : ''].join(shown && others ? ' ' : ''),
    total: perUnit || unknownCount ? null : reduce(known, (sum, service) => sum + (toNumber(service.price) || 0), 0),
  };
};
